import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import authService from '../../services/authService';
import { getUserType, isAdmin, normalizeRoles, isTeamUser, isEnterpriseUser } from '../../utils/roles';
import '../admin/Admin.css';
import './Auth.css';
import PlanCard from '../common/PlanCard';
import TokenPricing from '../common/TokenPricing';
import Toast from '../Toast';
import authenticationService from '../../services/authenticationService';

// Simple inline charts (no external deps)
function MiniLineChart({ points, height = 120, color = '#4c51bf', fill = 'rgba(76, 81, 191, 0.12)' }) {
  const containerRef = useRef(null);
  const [tip, setTip] = useState(null); // { x, y, label, value, idx }
  if (!points || points.length === 0) {
    return (
      <div style={{ height, display:'flex', alignItems:'center', justifyContent:'center', color:'#6b7280', fontSize:12 }}>No data</div>
    );
  }
  const width = Math.max(200, points.length * 36);
  const maxVal = Math.max(1, ...points.map(p => p.value));
  const minVal = 0;
  const xStep = width / Math.max(1, points.length - 1);
  const yScale = (v) => height - (v - minVal) / (maxVal - minVal || 1) * (height - 20) - 10;
  const path = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${i * xStep},${yScale(p.value)}`).join(' ');
  const area = `M 0,${height} ${path} L ${width},${height} Z`;
  const onMove = (e) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const xPx = e.clientX - rect.left;
    const xView = (xPx / rect.width) * width;
    const idx = Math.max(0, Math.min(points.length - 1, Math.round(xView / (xStep || 1))));
    const px = idx * xStep;
    const py = yScale(points[idx].value);
    setTip({ x: px, y: py, label: points[idx].label, value: points[idx].value, idx });
  };
  const onLeave = () => setTip(null);
  const cw = containerRef.current?.clientWidth || 0;
  const tipLeft = tip ? Math.min(Math.max(8, (tip.x / width) * (cw || 0) + 10), (cw || 0) - 160) : 0;
  const tipTop = tip ? Math.max(8, (tip.y / height) * height - 34) : 0;
  // Sparse ticks
  const xTickIdx = [0, Math.round((points.length - 1) / 2), points.length - 1].filter((v, i, arr) => v >= 0 && v < points.length && arr.indexOf(v) === i);
  const yTickVals = [0, Math.round(maxVal * 0.5), maxVal];
  return (
    <div ref={containerRef} style={{ position: 'relative', border:'1px solid #bfdbfe', borderRadius:8, background:'#eff6ff' }}>
      <svg viewBox={`0 0 ${width} ${height}`} style={{ width: '100%', height }} onMouseMove={onMove} onMouseLeave={onLeave}>
        {/* Y gridlines and labels */}
        {yTickVals.map((v, idx) => (
          <g key={`y-${idx}`}>
            <line x1={0} x2={width} y1={yScale(v)} y2={yScale(v)} stroke="#e0efff" />
            <text x={4} y={yScale(v) - 4} fontSize="10" fill="#64748b">{v}</text>
          </g>
        ))}
        <path d={area} fill={fill} stroke="none" />
        <path d={path} fill="none" stroke={color} strokeWidth="2" />
        {/* X ticks */}
        {xTickIdx.map((i) => (
          <text key={`x-${i}`} x={i * xStep} y={height - 2} fontSize="10" textAnchor="middle" fill="#64748b">{points[i].label}</text>
        ))}
        {tip && (
          <g>
            <line x1={tip.x} x2={tip.x} y1={0} y2={height} stroke="#9ca3af" strokeDasharray="4 4" />
            <circle cx={tip.x} cy={tip.y} r={3.5} fill={color} />
          </g>
        )}
      </svg>
      {tip && (
        <div style={{ position:'absolute', left: tipLeft, top: tipTop, background:'#111827', color:'#fff', fontSize:12, padding:'6px 8px', borderRadius:6, boxShadow:'0 4px 12px rgba(0,0,0,0.2)', pointerEvents:'none' }}>
          <div style={{ fontWeight:600 }}>{tip.label}</div>
          <div>Requests: {tip.value.toLocaleString()}</div>
        </div>
      )}
    </div>
  );
}

function MiniBarChart({ items, height = 160, color = '#059669' }) {
  const containerRef = useRef(null);
  const [tip, setTip] = useState(null); // { x, y, label, value }
  if (!items || items.length === 0) {
    return (
      <div style={{ height, display:'flex', alignItems:'center', justifyContent:'center', color:'#6b7280', fontSize:12 }}>No data</div>
    );
  }
  const n = items.length;
  const width = Math.max(240, n * 56);
  const maxVal = Math.max(1, ...items.map(i => i.value));
  const barW = Math.max(22, Math.floor(width / Math.max(1, n * 2)));
  const onMoveBar = (e, label, value) => {
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const xPx = e.clientX - rect.left;
    const yPx = e.clientY - rect.top;
    setTip({ x: xPx, y: yPx, label, value });
  };
  const onLeave = () => setTip(null);
  const cw = containerRef.current?.clientWidth || 0;
  const tipLeft = tip ? Math.min(Math.max(8, tip.x + 10), (cw || 0) - 180) : 0;
  const tipTop = tip ? Math.max(8, tip.y - 34) : 0;
  return (
    <div ref={containerRef} style={{ position:'relative', border:'1px solid #bfdbfe', borderRadius:8, background:'#eff6ff' }}>
      <svg viewBox={`0 0 ${width} ${height}`} style={{ width: '100%', height }} onMouseLeave={onLeave}>
        {items.map((i, idx) => {
          const h = Math.round((i.value / (maxVal || 1)) * (height - 24));
          const x = 14 + idx * (barW + 20);
          const y = height - h - 12;
          return (
            <g key={i.label}>
              <rect x={x} y={y} width={barW} height={h} fill={color} rx="4" onMouseMove={(e)=>onMoveBar(e, i.label, i.value)} />
            </g>
          );
        })}
      </svg>
      {tip && (
        <div style={{ position:'absolute', left: tipLeft, top: tipTop, background:'#111827', color:'#fff', fontSize:12, padding:'6px 8px', borderRadius:6, boxShadow:'0 4px 12px rgba(0,0,0,0.2)', pointerEvents:'none', maxWidth: 180 }}>
          <div style={{ fontWeight:600, marginBottom:2, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{tip.label}</div>
          <div>Count: {tip.value.toLocaleString()}</div>
        </div>
      )}
    </div>
  );
}

const presets = [
  { key: '1d', label: '1d', days: 1 },
  { key: '7d', label: '7d', days: 7 },
  { key: '30d', label: '30d', days: 30 }
];

const dollars = (cents, currency='USD') => {
  try {
    const n = (cents || 0) / 100;
    return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(n);
  } catch {
    return `$${((cents || 0) / 100).toFixed(2)}`;
  }
};

const NewDashboard = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [user, setUser] = useState(null);
  const [range, setRange] = useState('7d');
  const [summary, setSummary] = useState(null);
  const [ledger, setLedger] = useState({ total: 0, page: 1, pageSize: 50, items: [] });
  const [balance, setBalance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showContact, setShowContact] = useState(false);
  const [activeSettings, setActiveSettings] = useState({ lowBalanceCents: 50 });
  const [showLowBalanceModal, setShowLowBalanceModal] = useState(false);
  const [historyFilters, setHistoryFilters] = useState({ provider: '', model: '', reason: '', minCost: '', maxCost: '', start: '', end: '' });
  const [copiedUserId, setCopiedUserId] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const [confirmToast, setConfirmToast] = useState({ show: false, message: '', onConfirm: null, onCancel: null });
  const navigate = useNavigate();

  const computeDates = useCallback(() => {
    const now = new Date();
    const preset = presets.find(p => p.key === range);
    if (!preset) return { s: null, e: null };
    const e = now;
    const s = new Date(now.getTime() - preset.days * 24 * 60 * 60 * 1000);
    return { s, e };
  }, [range]);

  const loadSummaryAndLedger = useCallback(async () => {
    try {
      setLoading(true);
      const { s, e } = computeDates();
      const qs = new URLSearchParams();
      qs.set('page', String(1));
      qs.set('pageSize', String(500));
      if (s) qs.set('start', s.toISOString());
      if (e) qs.set('end', e.toISOString());
      
      // Use authenticatedFetch which handles token refresh automatically
      const [rUser, rSummary, rLedger, rBalance] = await Promise.all([
        authService.getUserInfo().catch(() => null), // getUserInfo has its own retry logic
        authService.authenticatedFetch(`/api/billing/usage-summary?${qs.toString()}`, { credentials: 'include' }).catch(() => ({ ok: false, status: 401 })),
        authService.authenticatedFetch(`/api/billing/ledger?${qs.toString()}`, { credentials: 'include' }).catch(() => ({ ok: false, status: 401 })),
        authService.authenticatedFetch('/api/billing/balance', { credentials: 'include' }).catch(() => ({ ok: false, status: 401 }))
      ]);
      
      // Check for 401 errors - if all fail, redirect to login
      if ((!rSummary || rSummary.status === 401) || (!rLedger || rLedger.status === 401) || (!rBalance || rBalance.status === 401)) {
        // Only logout if getUserInfo also failed (indicates session is truly expired)
        if (!rUser) {
          authService.logout();
          window.location.href = '/login';
          return;
        }
        // If getUserInfo succeeded but other calls failed, just show empty data
      }
      
      setUser(rUser);
      if (rSummary && rSummary.ok) {
        try {
          setSummary(await rSummary.json());
        } catch {}
      }
      if (rLedger && rLedger.ok) {
        try {
          setLedger(await rLedger.json());
        } catch {}
      }
      let balData = null;
      if (rBalance && rBalance.ok) {
        try {
          balData = await rBalance.json();
          setBalance(balData);
        } catch {}
      }
      // Load settings from localStorage (per user key)
      try {
        const uid = (rUser?.id || rUser?.userId || '').toString();
        const key = uid ? `dashSettings:${uid}` : 'dashSettings';
        // Prefer server threshold if available
        let serverLb = null;
        try {
          const t = authService.getToken && authService.getToken();
          const res = await fetch('/api/billing/low-balance-threshold', { headers: t ? { Authorization: `Bearer ${t}` } : {}, credentials: 'include' });
          if (res.ok) {
            const j = await res.json();
            serverLb = j?.lowBalanceWarnCents ?? null;
          }
        } catch {}
        const raw = localStorage.getItem(key);
        const parsed = raw ? JSON.parse(raw) : null;
        const localLb = parsed?.lowBalanceCents ?? null;
        const lb = Math.max(50, Math.min(5000, (serverLb ?? localLb ?? 50)));
        setActiveSettings({ lowBalanceCents: lb });
        // Load user preferences for default landing tab/date range and model prefs
        try {
          const t = authService.getToken && authService.getToken();
          const res = await fetch('/api/userpreferences', { headers: t ? { Authorization: `Bearer ${t}` } : {}, credentials: 'include' });
          if (res.ok) {
            const prefs = await res.json();
            if (prefs?.DefaultDateRange && (prefs.DefaultDateRange==='1d' || prefs.DefaultDateRange==='7d' || prefs.DefaultDateRange==='30d')) setRange(prefs.DefaultDateRange);
            if (prefs?.DefaultLandingTab && ['overview','analytics','history','insights','settings'].includes(prefs.DefaultLandingTab)) setActiveTab(prefs.DefaultLandingTab);
            if (prefs?.DefaultProvider) setPrefProvider(prefs.DefaultProvider);
            if (prefs?.DefaultModel) setPrefModel(prefs.DefaultModel);
            if (prefs?.DefaultMaxOutputTokens) setPrefMaxTokens(String(prefs.DefaultMaxOutputTokens));
            if (typeof prefs?.DefaultSavedPromptIsPublic === 'boolean') setPrefDefaultPromptVisibility(prefs.DefaultSavedPromptIsPublic);
            if (prefs?.PreferredCurrency) setPrefCurrency(prefs.PreferredCurrency);
            if (prefs?.PreferredTimezone) setPrefTimezone(prefs.PreferredTimezone);
            if (prefs?.DefaultExportFormat) setPrefExportFormat(prefs.DefaultExportFormat);
            if (typeof prefs?.EmailLowBalance === 'boolean') setPrefEmailLow(!!prefs.EmailLowBalance);
            if (typeof prefs?.EmailWeeklySummary === 'boolean') setPrefEmailWeekly(!!prefs.EmailWeeklySummary);
            if (typeof prefs?.EmailAnomalySpikes === 'boolean') setPrefEmailAnomaly(!!prefs.EmailAnomalySpikes);
            if (typeof prefs?.EmailReceiptOnPayment === 'boolean') setPrefEmailReceipt(!!prefs.EmailReceiptOnPayment);
            if (typeof prefs?.PasswordRotationDays === 'number') setPrefPasswordRotation(String(prefs.PasswordRotationDays > 0 ? prefs.PasswordRotationDays : 'none'));
          }
        } catch {}
        finally { setPrefsLoaded(true); }
        // Show low-balance modal only for current paid tiers
        const roles = (rUser?.roles || []).map(r => (r||'').toLowerCase());
        const isTeam = roles.includes('teamuser') || roles.includes('teamadmin');
        const isEnt = roles.includes('enterpriseuser') || roles.includes('enterpriseadmin');
        const isAdmin = roles.includes('admin');
        let isPaidEffective = isTeam || isEnt || isAdmin;
        if (!isPaidEffective && roles.includes('paiduser')) {
          const balCents = typeof balData?.balanceCents === 'number' ? balData.balanceCents : 0;
          isPaidEffective = balCents > 0;
        }
        const dismissed = sessionStorage.getItem('lowBalanceDismissed') === 'true';
        if (isPaidEffective && balData && !dismissed && balData.balanceCents <= lb) {
          setShowLowBalanceModal(true);
        }
      } catch {}
    } catch (e) {
      setError(e.message || 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }, [computeDates]);

  useEffect(() => { loadSummaryAndLedger(); }, [loadSummaryAndLedger, range]);

  // Handle Revoke All Sessions
  const handleRevokeAllSessions = () => {
    const handleConfirm = async () => {
      setConfirmToast(prev => ({ ...prev, show: false }));
      try {
        await authenticationService.revokeAllSessions();
        setToast({ show: true, type: 'success', message: 'All sessions revoked. Logging out...' });
        setTimeout(async () => {
          await authService.logout();
          navigate('/login');
        }, 1500);
      } catch (err) {
        setToast({ show: true, type: 'error', message: err.message || 'Failed to revoke all sessions. Please try again.' });
      }
    };

    const handleCancel = () => {
      setConfirmToast(prev => ({ ...prev, show: false }));
    };

    setConfirmToast({
      show: true,
      message: 'Are you sure you want to revoke all sessions? You will be logged out.',
      onConfirm: handleConfirm,
      onCancel: handleCancel
    });
  };

  // Refresh dashboard data when window regains focus or a balance refresh signal is emitted
  useEffect(() => {
    const onFocus = () => { try { loadSummaryAndLedger(); } catch {} };
    const onStorage = (e) => {
      try {
        if (e && e.key === 'balanceRefreshTick') { loadSummaryAndLedger(); }
        if (e && e.key === 'justUpgradedPro' && e.newValue === '1') {
          try { localStorage.removeItem('justUpgradedPro'); } catch {}
          try {
            setToast({ show: true, type: 'success', message: 'Welcome to Pro! Enjoy enhanced features and higher limits.' });
          } catch {}
        }
      } catch {}
    };
    // Listen for low balance dismissal from other components (e.g., GlobalAlerts)
    const onLowBalanceDismissed = () => {
      setShowLowBalanceModal(false);
    };
    window.addEventListener('focus', onFocus);
    window.addEventListener('storage', onStorage);
    window.addEventListener('lowBalanceDismissed', onLowBalanceDismissed);
    return () => {
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('lowBalanceDismissed', onLowBalanceDismissed);
    };
  }, [loadSummaryAndLedger]);

  // Model preferences state (declare before effects that use them)
  const [modelProviders, setModelProviders] = useState([]);
  const [prefProvider, setPrefProvider] = useState('');
  const [prefModel, setPrefModel] = useState('');
  const [prefMaxTokens, setPrefMaxTokens] = useState('');
  const [prefCurrency, setPrefCurrency] = useState('USD');
  const [prefTimezone, setPrefTimezone] = useState('');
  const [prefEmailLow, setPrefEmailLow] = useState(false);
  const [prefEmailWeekly, setPrefEmailWeekly] = useState(false);
  const [prefEmailAnomaly, setPrefEmailAnomaly] = useState(false);
  const [prefEmailReceipt, setPrefEmailReceipt] = useState(false);
  const [prefPasswordRotation, setPrefPasswordRotation] = useState('none');
  const [timezoneOptions, setTimezoneOptions] = useState([]);
  const [tzFilter, setTzFilter] = useState('');
  const [prefDefaultPromptVisibility, setPrefDefaultPromptVisibility] = useState(null);
  const [prefExportFormat, setPrefExportFormat] = useState('csv');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showDeleting, setShowDeleting] = useState(false);
  const [showDeletedModal, setShowDeletedModal] = useState(false);
  const [_deleteResult, setDeleteResult] = useState({ ok:false, deletionId:'', message:'' });
  const [prefsLoaded, setPrefsLoaded] = useState(false);

  // Load model providers for Model Preferences
  useEffect(() => {
    (async () => {
      try {
        const resp = await fetch('/api/ai/models');
        if (!resp.ok) return;
        const data = await resp.json();
        const items = (data?.providers || []).map(p => ({ key: p.provider, name: p.provider, defaultModel: p.defaultModel, allowed: p.allowedModels || [] }));
        setModelProviders(items);
        if (prefsLoaded && items.length > 0) {
          // If DefaultModel is known but provider is not, derive provider from model membership
          if (!prefProvider && prefModel) {
            const provByModel = items.find(i => (i.allowed || []).some(m => String(m).toLowerCase() === String(prefModel).toLowerCase()))
              || items.find(i => String(i.defaultModel||'').toLowerCase() === String(prefModel).toLowerCase())
              || null;
            if (provByModel) setPrefProvider(provByModel.key);
          }
          // If still no provider, fallback to first
          if (!prefProvider) setPrefProvider(items[0].key);
          // If provider exists but model is empty or not in allowed list, choose provider default
          const currentProv = items.find(i => i.key === (prefProvider || items[0].key)) || items[0];
          const allowed = currentProv.allowed || [];
          const hasModel = allowed.some(m => String(m).toLowerCase() === String(prefModel||'').toLowerCase());
          if (!prefModel || !hasModel) {
            setPrefModel(currentProv.defaultModel || (allowed[0] || ''));
          }
        }
      } catch {}
    })();
  }, [prefProvider, prefModel, prefsLoaded]);

  // Build timezone options (global) with UTC offset labels
  useEffect(() => {
    (async () => {
      try {
        const zones = (typeof Intl !== 'undefined' && Intl.supportedValuesOf) ? Intl.supportedValuesOf('timeZone') : [
          'UTC','America/New_York','America/Chicago','America/Denver','America/Los_Angeles','Europe/London','Europe/Berlin','Europe/Paris','Asia/Shanghai','Asia/Tokyo','Asia/Kolkata','Asia/Seoul','Australia/Sydney'
        ];
        const now = new Date();
        const items = zones.map(tz => {
          let off = 'UTC';
          try {
            const s = new Intl.DateTimeFormat('en-US', { timeZone: tz, timeZoneName: 'shortOffset', hour: '2-digit' }).format(now);
            const m = s.match(/GMT([+-]\d{1,2})(?::(\d{2}))?/);
            off = m ? `UTC${m[1]}${m[2] ? ':' + m[2] : ''}` : 'UTC';
          } catch {}
          const city = tz.split('/').slice(-1)[0].replace(/_/g, ' ');
          return { tz, label: `${city} (${off})` };
        }).sort((a,b) => a.label.localeCompare(b.label));
        setTimezoneOptions(items);
      } catch { setTimezoneOptions([]); }
    })();
  }, []);

  const effectiveTimezoneLabel = useMemo(() => {
    try {
      const tz = prefTimezone || (Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC');
      const now = new Date();
      let off = 'UTC';
      try {
        const s = new Intl.DateTimeFormat('en-US', { timeZone: tz, timeZoneName: 'shortOffset', hour: '2-digit' }).format(now);
        const m = s.match(/GMT([+-]\d{1,2})(?::(\d{2}))?/);
        off = m ? `UTC${m[1]}${m[2] ? ':' + m[2] : ''}` : 'UTC';
      } catch {}
      const city = tz.split('/').slice(-1)[0].replace(/_/g, ' ');
      return `${city} (${off})${prefTimezone ? '' : ' • using system default'}`;
    } catch { return 'UTC'; }
  }, [prefTimezone]);

  const dailySeries = useMemo(() => {
    const byDay = new Map();
    (ledger.items || []).forEach(i => {
      const d = new Date(i.occurredAt);
      const k = d.toISOString().slice(0, 10);
      const prev = byDay.get(k) || { requests: 0, costCents: 0 };
      byDay.set(k, { requests: prev.requests + 1, costCents: prev.costCents + (i.costCents || 0) });
    });
    // Fill date bins for selected preset
    const { s, e } = computeDates();
    let cursor = s ? new Date(Date.UTC(s.getUTCFullYear(), s.getUTCMonth(), s.getUTCDate())) : null;
    const end = e ? new Date(Date.UTC(e.getUTCFullYear(), e.getUTCMonth(), e.getUTCDate())) : null;
    const ordered = [];
    if (cursor && end) {
      while (cursor <= end) {
        const key = cursor.toISOString().slice(0,10);
        const v = byDay.get(key) || { requests: 0, costCents: 0 };
        ordered.push([key, v]);
        cursor = new Date(cursor.getTime() + 24*60*60*1000);
      }
    } else {
      ordered.push(...Array.from(byDay.entries()).sort((a,b) => a[0].localeCompare(b[0])));
    }
    return {
      requests: ordered.map(([k, v]) => ({ label: k.slice(5), value: v.requests })),
      cost: ordered.map(([k, v]) => ({ label: k.slice(5), value: Math.round((v.costCents || 0) / 100) }))
    };
  }, [ledger, computeDates]);

  const modelBreakdown = useMemo(() => {
    const byModel = new Map();
    (ledger.items || []).forEach(i => {
      const m = i.model || '—';
      byModel.set(m, (byModel.get(m) || 0) + 1);
    });
    return Array.from(byModel.entries())
      .map(([label, value]) => ({ label, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 8);
  }, [ledger]);

  const totals = useMemo(() => {
    const totalRequests = summary?.totalRequests || (ledger.items || []).length;
    const totalCostCents = (ledger.items || []).reduce((acc, i) => acc + (i.costCents || 0), 0);
    const avgCostPerRequest = totalRequests ? totalCostCents / totalRequests : 0;
    const topModel = modelBreakdown.length ? modelBreakdown[0].label : '—';
    let daysRemaining = '—';
    const bal = balance?.balanceCents || 0;
    // Average daily spend from dailySeries cost
    const dailyCosts = dailySeries.cost.map(p => p.value);
    const avgDailySpend = dailyCosts.length ? (dailyCosts.reduce((a, b) => a + b, 0) / dailyCosts.length) : 0;
    if (bal > 0 && avgDailySpend > 0) {
      daysRemaining = Math.max(1, Math.floor((bal / 100) / avgDailySpend));
    }
    return { totalRequests, avgCostPerRequest, topModel, balanceCents: bal, daysRemaining };
  }, [summary, ledger, modelBreakdown, balance, dailySeries]);

  const userType = useMemo(() => {
    // Derive display type from roles AND current balance
    try {
      if (getUserType && isAdmin && normalizeRoles) {
        if (isAdmin(user)) return 'Admin';
        const roles = normalizeRoles(user);
        // Check for Enterprise roles first (highest priority)
        if (roles.includes('enterpriseadmin')) return 'EnterpriseAdmin';
        if (roles.includes('enterpriseuser')) return 'EnterpriseUser';
        // Check for Team roles
        if (roles.includes('teamadmin')) return 'TeamAdmin';
        if (roles.includes('teamuser')) return 'TeamUser';
        // Check for Pro role (PaidUser)
        const isProRole = roles.includes('paiduser');
        const bal = (balance && typeof balance.balanceCents === 'number') ? balance.balanceCents : null;
        if (isProRole) {
          if (bal !== null && bal <= 0) return 'Free';
          return 'Pro';
        }
        return 'Free';
      }
    } catch {}
    return getUserType ? getUserType(user) : 'Free';
  }, [user, balance]);

  // eslint-disable-next-line no-unused-vars
  const _startCheckout = useCallback(async (opts) => {
    // Placeholder for Stripe integration. For now, route to top-up page.
    try {
      // opts can be { plan: 'pro' } or { amountCents: 5000 }
      navigate('/top-up');
    } catch {}
  }, [navigate]);

  const filteredLedger = useMemo(() => {
    const minC = historyFilters.minCost ? parseFloat(historyFilters.minCost) : null;
    const maxC = historyFilters.maxCost ? parseFloat(historyFilters.maxCost) : null;
    const start = historyFilters.start ? new Date(historyFilters.start) : null;
    const end = historyFilters.end ? new Date(historyFilters.end) : null;
    const parseUtcDate = (val) => {
      if (!val) return new Date(NaN);
      if (val instanceof Date) return val;
      const s = String(val);
      const hasTz = /Z$/i.test(s) || /[+-]\d{2}:?\d{2}$/.test(s);
      return new Date(hasTz ? s : s + 'Z');
    };
    return (ledger.items || []).filter(i => {
      if (historyFilters.provider && i.provider !== historyFilters.provider) return false;
      if (historyFilters.model && i.model !== historyFilters.model) return false;
      if (historyFilters.reason && !(i.reason || '').toLowerCase().includes(historyFilters.reason.toLowerCase())) return false;
      const cost = (i.costCents || 0) / 100;
      if (minC != null && cost < minC) return false;
      if (maxC != null && cost > maxC) return false;
      const t = parseUtcDate(i.occurredAt);
      if (start && t < start) return false;
      if (end && t > end) return false;
      return true;
    });
  }, [ledger, historyFilters]);

  const exportCsv = useCallback(() => {
    const header = ['Date', 'Provider', 'Model', 'InputTokens', 'OutputTokens', 'Cost', 'BalanceAfter', 'Reason'];
    const rows = filteredLedger.map(i => [
      new Date(i.occurredAt).toISOString(),
      i.provider || '',
      i.model || '',
      i.inputTokens ?? '',
      i.outputTokens ?? '',
      ((i.costCents || 0) / 100).toFixed(4),
      i.balanceAfterCents != null ? ((i.balanceAfterCents || 0) / 100).toFixed(2) : '',
      (i.reason || '').replace(/\n/g, ' ')
    ]);
    const csv = [header, ...rows].map(r => r.map(v => {
      const s = String(v);
      return s.includes(',') || s.includes('"') ? `"${s.replace(/"/g, '""')}"` : s;
    }).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `usage-history-${Date.now()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [filteredLedger]);

  const efficiency = useMemo(() => {
    const totalIn = (ledger.items || []).reduce((a, i) => a + (i.inputTokens || 0), 0);
    const totalOut = (ledger.items || []).reduce((a, i) => a + (i.outputTokens || 0), 0);
    const totalCost = (ledger.items || []).reduce((a, i) => a + (i.costCents || 0), 0) / 100;
    const outPerDollar = totalCost > 0 ? Math.round(totalOut / totalCost) : 0;
    const inPerDollar = totalCost > 0 ? Math.round(totalIn / totalCost) : 0;
    return { totalIn, totalOut, totalCost, outPerDollar, inPerDollar };
  }, [ledger]);

  const [categoryItems, setCategoryItems] = useState([]);
  const [categoryLoading, setCategoryLoading] = useState(false);
  const [categoryMetric, setCategoryMetric] = useState(() => sessionStorage.getItem('dashCategoryMetric') || 'cost'); // 'cost' | 'requests'
  useEffect(() => {
    (async () => {
      try {
        setCategoryLoading(true);
        const token = authService.getToken && authService.getToken();
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const { s, e } = computeDates();
        const qs = new URLSearchParams();
        if (s) qs.set('start', s.toISOString());
        if (e) qs.set('end', e.toISOString());
        const r = await fetch(`/api/billing/category-breakdown?${qs.toString()}`, { headers, credentials: 'include' });
        if (r.ok) setCategoryItems(await r.json()); else setCategoryItems([]);
      } catch { setCategoryItems([]); }
      finally { setCategoryLoading(false); }
    })();
  }, [computeDates]);

  useEffect(() => {
    if (!showDeletedModal) return;
    const t = setTimeout(async() => { try { await authService.logout(); window.location.href = '/login'; } catch {} }, 10000);
    return () => clearTimeout(t);
  }, [showDeletedModal]);

  if (loading) {
    return (
      <div className="auth-container">
        <div className="auth-card"><div className="loading-spinner">Loading...</div></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="auth-container">
        <div className="auth-card"><div className="error-message">{error}</div></div>
      </div>
    );
  }

  return (
    <div className="admin-container">
      <div className="admin-subnav">
        <button className={activeTab==='overview'?'active':''} onClick={()=>setActiveTab('overview')}>Overview</button>
        <button className={activeTab==='analytics'?'active':''} onClick={()=>setActiveTab('analytics')}>Analytics</button>
        <button className={activeTab==='history'?'active':''} onClick={()=>setActiveTab('history')}>History</button>
        <button className={activeTab==='insights'?'active':''} onClick={()=>setActiveTab('insights')}>Insights</button>
        <button className={activeTab==='settings'?'active':''} onClick={()=>setActiveTab('settings')}>Settings</button>
      </div>

      {/* Range Picker */}
      <div className="admin-section" style={{ marginTop: 0 }}>
        <div style={{ display:'flex', alignItems:'center', gap:8, justifyContent:'space-between', flexWrap:'wrap' }}>
          <div style={{ display:'flex', gap:8 }}>
            {presets.map(p => (
              <button key={p.key} className={range===p.key?'primary':''} onClick={()=>setRange(p.key)}>{p.label}</button>
            ))}
          </div>
          <div style={{ display:'flex', gap:16, color:'#555' }}>
            <div><strong>Balance:</strong> <span style={{ color: (balance && balance.balanceCents <= (activeSettings.lowBalanceCents||50)) ? '#b91c1c' : undefined }}>{balance ? dollars(balance.balanceCents, prefCurrency) : '—'}</span></div>
            <div><strong>Today:</strong> {balance ? dollars(balance.spendTodayCents, prefCurrency) : '—'}</div>
          </div>
        </div>
      </div>

      {activeTab === 'overview' && (
        <div className="admin-section">
          <h2 className="admin-header">Overview</h2>
          {/* Account Block + Actions */}
          <div className="dashboard-card" style={{ marginBottom: 12 }}>
            <h2>{user?.firstName && user?.lastName ? `${user.firstName} ${user.lastName}'s Account` : 'Your Account'}</h2>
            <div style={{ marginTop: 10, display:'flex', gap:8, borderBottom:'1px solid #e5e7eb', paddingBottom:6, flexWrap:'wrap' }}>
              <button 
                style={{
                  background: '#f3f4f6',
                  color: '#111827',
                  border: '1px solid #e5e7eb',
                  borderBottom: 'none',
                  borderRadius: '6px 6px 0 0',
                  padding: '6px 10px',
                  cursor: 'pointer',
                  fontWeight: 400,
                  fontSize: '14px'
                }}
                onClick={() => navigate('/saved-prompts')}
              >
                My Saved Prompts
              </button>
              <button 
                style={{
                  background: '#f3f4f6',
                  color: '#111827',
                  border: '1px solid #e5e7eb',
                  borderBottom: 'none',
                  borderRadius: '6px 6px 0 0',
                  padding: '6px 10px',
                  cursor: 'pointer',
                  fontWeight: 400,
                  fontSize: '14px'
                }}
                onClick={() => navigate('/scripts-library')}
              >
                Scripts Library
              </button>
              <button 
                style={{
                  background: '#f3f4f6',
                  color: '#111827',
                  border: '1px solid #e5e7eb',
                  borderBottom: 'none',
                  borderRadius: '6px 6px 0 0',
                  padding: '6px 10px',
                  cursor: 'pointer',
                  fontWeight: 400,
                  fontSize: '14px'
                }}
                onClick={() => navigate('/settings/billing')}
              >
                Billing ($)
              </button>
              <button 
                style={{
                  background: '#f3f4f6',
                  color: '#111827',
                  border: '1px solid #e5e7eb',
                  borderBottom: 'none',
                  borderRadius: '6px 6px 0 0',
                  padding: '6px 10px',
                  cursor: 'pointer',
                  fontWeight: 400,
                  fontSize: '14px'
                }}
                onClick={() => navigate('/settings/security')}
              >
                Security (MFA)
              </button>
              <button 
                style={{
                  background: '#f3f4f6',
                  color: '#111827',
                  border: '1px solid #e5e7eb',
                  borderBottom: 'none',
                  borderRadius: '6px 6px 0 0',
                  padding: '6px 10px',
                  cursor: 'pointer',
                  fontWeight: 400,
                  fontSize: '14px'
                }}
                onClick={() => setShowContact(true)}
              >
                Contact
              </button>
              <button 
                style={{
                  background: '#fee2e2',
                  color: '#111827',
                  border: '1px solid #fecaca',
                  borderBottom: 'none',
                  borderRadius: '6px 6px 0 0',
                  padding: '6px 10px',
                  cursor: 'pointer',
                  fontWeight: 400,
                  fontSize: '14px'
                }}
                onClick={handleRevokeAllSessions}
              >
                Revoke All Sessions
              </button>
              {/* Hide Upgrade to Pro button for Team/Enterprise users and Admins */}
              {(() => {
                const hideUpgradeButton = userType === 'Admin' || userType === 'TeamUser' || userType === 'TeamAdmin' || userType === 'EnterpriseUser' || userType === 'EnterpriseAdmin';
                if (hideUpgradeButton) return null;
                
                return (
                  <button
                    type="button"
                    style={{
                      background: '#dbeafe',
                      color: '#111827',
                      border: '1px solid #bfdbfe',
                      borderBottom: 'none',
                      borderRadius: '6px 6px 0 0',
                      padding: '6px 10px',
                      cursor: 'pointer',
                      fontWeight: 400,
                      fontSize: '14px'
                    }}
                    onClick={()=>{ const td = (() => { try { const em = (user?.email||'').toLowerCase(); return em.startsWith('testdrive'); } catch { return false; } })(); if (td) return; navigate('/top-up'); }}
                    title="Upgrade to Pro or Top Up"
                    disabled={(() => { try { const em = (user?.email||'').toLowerCase(); return em.startsWith('testdrive'); } catch { return false; } })()}
                  >
                    {(() => { try { const em = (user?.email||'').toLowerCase(); return em.startsWith('testdrive'); } catch { return false; } })() ? 'Top-ups disabled (TestDrive)' : ((userType==='Pro' || userType==='Admin') ? 'Top Up' : 'Upgrade to Pro')}
                  </button>
                );
              })()}
            </div>
            <div className="user-info" style={{ display:'grid', gridTemplateColumns:'1fr 1fr', columnGap:16 }}>
              <div className="info-item"><strong>Name:</strong> {user?.firstName} {user?.lastName}</div>
              <div className="info-item"><strong>Member since:</strong> {user?.registrationDate ? new Date(user.registrationDate).toLocaleDateString(undefined, prefTimezone ? { timeZone: prefTimezone } : undefined) : '—'}</div>
              <div className="info-item"><strong>Email:</strong> {user?.email}</div>
              <div className="info-item"><strong>Last login:</strong> {user?.lastLoginDate ? new Date(user.lastLoginDate).toLocaleDateString(undefined, prefTimezone ? { timeZone: prefTimezone } : undefined) : '—'}</div>
              <div className="info-item"><strong>User Type:</strong> {userType}</div>
              <div className="info-item" style={{ display:'flex', alignItems:'center', gap:8 }}>
                <strong>UID:</strong>
                <span style={{ flex:1, overflow:'hidden', whiteSpace:'nowrap', textOverflow:'ellipsis', fontFamily:'ui-monospace, Menlo, Monaco, Consolas, "Courier New", monospace' }}>{(user?.id || user?.userId || '')}</span>
                <button
                  type="button"
                  aria-label="Copy user id"
                  title={copiedUserId ? 'Copied!' : 'Copy'}
                  onClick={async()=>{
                    try {
                      const id = (user?.id || user?.userId || '') + '';
                      if (id) { await navigator.clipboard.writeText(id); setCopiedUserId(true); setTimeout(()=>setCopiedUserId(false), 1200); }
                    } catch {}
                  }}
                  style={{ display:'inline-flex', alignItems:'center', justifyContent:'center', width:24, height:24, border:'1px solid #d1d5d8', borderRadius:6, background:'transparent', cursor:'pointer', padding:0 }}
                >
                  <span role="img" aria-hidden="true">📋</span>
                </button>
                {copiedUserId && <span style={{ fontSize:12, color:'#059669' }}>Copied</span>}
              </div>
              <div className="info-item"><strong>Email confirmed:</strong> <span className={user?.emailConfirmed ? 'status-confirmed' : 'status-pending'}>{user?.emailConfirmed ? ' ✓ Confirmed' : ' ⚠ Pending'}</span></div>
            </div>
          </div>

          {/* Summary Cards */}
          <div className="admin-stats-grid" style={{ marginBottom: 12 }}>
            <div className="admin-stat-item"><div className="admin-stat-number">{totals.totalRequests}</div><div className="admin-stat-label">Requests (period)</div></div>
            <div className="admin-stat-item"><div className="admin-stat-number">{dollars(Math.round(totals.avgCostPerRequest))}</div><div className="admin-stat-label">Avg Cost / Request</div></div>
            <div className="admin-stat-item"><div className="admin-stat-number">{totals.topModel}</div><div className="admin-stat-label">Top Model</div></div>
            <div className="admin-stat-item"><div className="admin-stat-number" style={{ color: (balance && typeof balance.balanceCents==='number' && balance.balanceCents <= (activeSettings.lowBalanceCents||50)) ? '#b91c1c' : undefined }}>{balance ? dollars(balance.balanceCents, prefCurrency) : '—'}</div><div className="admin-stat-label">Credits Remaining</div></div>
            <div className="admin-stat-item"><div className="admin-stat-number">{totals.daysRemaining}</div><div className="admin-stat-label">Days Until Depletion</div></div>
          </div>

          {/* Charts */}
          <div style={{ display:'grid', gridTemplateColumns:'2fr 1fr', gap:12 }}>
            <div className="dashboard-card">
              <h3 style={{ marginTop: 0 }}>Requests Over Time</h3>
              <MiniLineChart points={dailySeries.requests} />
            </div>
            <div className="dashboard-card">
              <h3 style={{ marginTop: 0 }}>Model Distribution</h3>
              <MiniBarChart items={modelBreakdown.map(m => ({ label: m.label.split(':').slice(-1)[0], value: m.value }))} />
            </div>
          </div>

          {/* Plans & Pricing - Only show for Free/Pro users, hide for Team/Enterprise users and Admins */}
          {(() => {
            const hidePricing = userType === 'Admin' || userType === 'TeamUser' || userType === 'TeamAdmin' || userType === 'EnterpriseUser' || userType === 'EnterpriseAdmin';
            if (hidePricing) return null;
            
            return (
              <div className="dashboard-card" style={{ marginTop: 12 }}>
                <h3 style={{ marginTop: 0 }} aria-label="Plans and pricing">Plans & Pricing</h3>
            <div className="plan-grid">
              <PlanCard
                title="FREE"
                priceText="$0/month"
                isCurrent={!loading && userType==='Free'}
                features={[
                  'Prompt generation (unlimited)',
                  'Limited prompt optimizations',
                  'Up to 20 AI transactions/day',
                  '2 AI models',
                  'Work mode only',
                  'Basic export (txt only)',
                  'Public prompt sharing'
                ]}
              />
              <PlanCard
                title="PRO"
                subtitle="Pay-as-you-go"
                variant="pro"
                isCurrent={!loading && (userType==='Pro' || userType==='Admin')}
                features={[
                  'Unlimited prompt optimizations',
                  'All 11 AI models',
                  'Work + Chat modes',
                  '5 export formats',
                  'Enhanced dashboard & billing history'
                ]}
                tokenPricing={<TokenPricing />}
                ctaLabel={!loading ? ((() => { try { const em = (user?.email||'').toLowerCase(); return em.startsWith('testdrive'); } catch { return false; } })() ? 'Top-ups disabled (TestDrive)' : ((userType==='Pro' || userType==='Admin') ? 'Top Up' : 'Upgrade to Pro')) : 'Loading…'}
                onCtaClick={()=>{ const td = (() => { try { const em = (user?.email||'').toLowerCase(); return em.startsWith('testdrive'); } catch { return false; } })(); if (loading || td) return; navigate('/top-up'); }}
                ctaDisabled={!!((() => { try { const em = (user?.email||'').toLowerCase(); return em.startsWith('testdrive'); } catch { return false; } })())}
                ctaNote={( () => { const td = (() => { try { const em = (user?.email||'').toLowerCase(); return em.startsWith('testdrive'); } catch { return false; } })(); return td ? 'Top-ups are disabled for TestDrive accounts. Contact Admin for additional test credits.' : 'Checkout via Stripe.'; })()}
              />
              <PlanCard
                title="TEAMS"
                priceText="$15/user/month"
                subtitle="(5 user minimum = $75/month)"
                features={[
                  'Private team prompt sharing',
                  'Shotgun AI (multi-model queries)',
                  'Team admin controls',
                  'BAIQL usage'
                ]}
                ctaLabel="Contact sales"
                onCtaClick={()=>setShowContact(true)}
                //ctaNote={"Contact options coming soon."}
              />
              <PlanCard
                title="ENTERPRISE"
                priceText="Custom pricing"
                features={[
                  'Role-based permissions',
                  'Audit logs & compliance',
                  'Advanced BAIQL features',
                  'Dedicated support',
                  'Private deployment options'
                ]}
                ctaLabel="Contact sales"
                onCtaClick={()=>setShowContact(true)}
                //ctaNote={"Contact options coming soon."}
              />
            </div>
              </div>
            );
          })()}

        </div>
      )}

      {activeTab === 'analytics' && (
        <div className="admin-section">
          <h2 className="admin-header">Analytics</h2>
          <div style={{ display:'grid', gridTemplateColumns:'2fr 1fr', gap:12 }}>
            <div className="dashboard-card">
              <h3 style={{ marginTop: 0 }}>Cost Trends Over Time ({prefCurrency || 'USD'})</h3>
              <MiniLineChart points={dailySeries.cost} color="#ef4444" fill="rgba(239, 68, 68, 0.12)" />
            </div>
            <div className="dashboard-card">
              <h3 style={{ marginTop: 0 }}>Efficiency</h3>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(2,1fr)', gap:8 }}>
                <div className="admin-stat-item"><div className="admin-stat-number">{efficiency.totalOut.toLocaleString()}</div><div className="admin-stat-label">Tokens Out</div></div>
                <div className="admin-stat-item"><div className="admin-stat-number">{efficiency.totalIn.toLocaleString()}</div><div className="admin-stat-label">Tokens In</div></div>
                <div className="admin-stat-item"><div className="admin-stat-number">{dollars(Math.round(efficiency.totalCost*100), prefCurrency)}</div><div className="admin-stat-label">Cost (period)</div></div>
                <div className="admin-stat-item"><div className="admin-stat-number">{efficiency.outPerDollar.toLocaleString()}</div><div className="admin-stat-label">Out Tokens / $</div></div>
              </div>
            </div>
          </div>
          <div className="dashboard-card" style={{ marginTop: 12 }}>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
              <h3 style={{ marginTop: 0 }}>Category Breakdown</h3>
              <div style={{ display:'flex', gap:6 }}>
                <button className={categoryMetric==='cost'?'primary':''} onClick={()=>{setCategoryMetric('cost'); sessionStorage.setItem('dashCategoryMetric','cost');}}>Cost</button>
                <button className={categoryMetric==='requests'?'primary':''} onClick={()=>{setCategoryMetric('requests'); sessionStorage.setItem('dashCategoryMetric','requests');}}>Requests</button>
              </div>
            </div>
            {categoryLoading ? (
              <div style={{ color:'#64748b', fontSize:12 }}>Loading category data…</div>
            ) : categoryItems.length === 0 ? (
              <div style={{ color:'#64748b', fontSize:12 }}>No category data yet. This metric was just enabled and will populate after new requests. Try a wider date range.</div>
            ) : (
              <>
                <MiniBarChart items={categoryItems.map(c => ({ label: c.categoryName, value: categoryMetric==='cost' ? c.totalCostCents : c.requests }))} height={180} color={categoryMetric==='cost' ? '#2563eb' : '#059669'} />
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:8, marginTop:8 }}>
                  {categoryItems.map(c => (
                    <div key={c.categoryId} className="admin-stat-item">
                      <div className="admin-stat-number" style={{ fontSize:18 }}>{c.categoryName}</div>
                      <div className="admin-stat-label">Requests: {c.requests.toLocaleString()}</div>
                      <div className="admin-stat-label">Cost: {dollars(c.totalCostCents, prefCurrency)}</div>
                      <div className="admin-stat-label">{c.percentCost.toFixed(1)}% of spend</div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {activeTab === 'history' && (
        <div className="admin-section">
          <h2 className="admin-header">History</h2>
          <div className="admin-controls" style={{ flexWrap:'wrap', gap:8 }}>
            <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
              <input placeholder="Provider" value={historyFilters.provider} onChange={e=>setHistoryFilters(v=>({ ...v, provider: e.target.value }))} />
              <input placeholder="Model" value={historyFilters.model} onChange={e=>setHistoryFilters(v=>({ ...v, model: e.target.value }))} />
              <input placeholder="Reason contains" value={historyFilters.reason} onChange={e=>setHistoryFilters(v=>({ ...v, reason: e.target.value }))} />
              <input placeholder="Min $" type="number" step="0.01" value={historyFilters.minCost} onChange={e=>setHistoryFilters(v=>({ ...v, minCost: e.target.value }))} />
              <input placeholder="Max $" type="number" step="0.01" value={historyFilters.maxCost} onChange={e=>setHistoryFilters(v=>({ ...v, maxCost: e.target.value }))} />
              <input type="date" value={historyFilters.start} onChange={e=>setHistoryFilters(v=>({ ...v, start: e.target.value }))} />
              <input type="date" value={historyFilters.end} onChange={e=>setHistoryFilters(v=>({ ...v, end: e.target.value }))} />
            </div>
            <div style={{ display:'flex', gap:8 }}>
              <button onClick={exportCsv}>Export CSV</button>
              <button onClick={()=>setHistoryFilters({ provider:'', model:'', reason:'', minCost:'', maxCost:'', start:'', end:'' })}>Clear</button>
            </div>
          </div>
          <div className="table-scroll">
            <table className="admin-table">
              <thead><tr><th>Date</th><th>Provider</th><th>Model</th><th>In</th><th>Out</th><th>Cost</th><th>Balance</th><th>Reason</th></tr></thead>
              <tbody>
                {filteredLedger.map(i => (
                  <tr key={i.id}>
                    <td>{(() => { try { const s=String(i.occurredAt); const hasTz=/Z$/i.test(s)||(/[+-]\d{2}:?\d{2}$/.test(s)); const dt=new Date(hasTz?s:(s+'Z')); return new Intl.DateTimeFormat(undefined, { timeZone: (prefTimezone||undefined), year:'numeric', month:'2-digit', day:'2-digit', hour:'2-digit', minute:'2-digit', second:'2-digit', hour12:false, timeZoneName:'short' }).format(dt); } catch { return new Date(i.occurredAt).toLocaleString(); } })()}</td>
                    <td>{i.provider||''}</td>
                    <td>{i.model||''}</td>
                    <td>{i.inputTokens??''}</td>
                    <td>{i.outputTokens??''}</td>
                    <td>{dollars(i.costCents, prefCurrency)}</td>
                    <td>{i.balanceAfterCents!=null ? dollars(i.balanceAfterCents, prefCurrency) : '—'}</td>
                    <td>{i.reason||''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === 'insights' && (
        <div className="admin-section">
          <h2 className="admin-header">Insights</h2>
          <div className="dashboard-card">
            <h3 style={{ marginTop: 0 }}>Cost Optimization</h3>
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              <li>Consider batching small requests to reduce per-call overhead if applicable.</li>
              <li>Review your top model ({totals.topModel}) usage; compare with lower-cost models for similar output.</li>
              <li>Set alerts when daily spend exceeds average by 50%.</li>
            </ul>
          </div>
          <div className="dashboard-card" style={{ marginTop: 12 }}>
            <h3 style={{ marginTop: 0 }}>Usage Patterns</h3>
            <div>Peak request days in this period: {dailySeries.requests.slice().sort((a,b)=>b.value-a.value).slice(0,2).map(p=>p.label).join(', ') || '—'}</div>
          </div>
          <div className="dashboard-card" style={{ marginTop: 12 }}>
            <h3 style={{ marginTop: 0 }}>Predictions & Alerts</h3>
            <div>Estimated days until credit depletion: {totals.daysRemaining}</div>
          </div>
        </div>
      )}

      {activeTab === 'settings' && (
        <div className="admin-section">
          <h2 className="admin-header">Settings</h2>
          <div className="dashboard-card">
            <h3 style={{ marginTop: 0 }}>Low Balance Warning</h3>
            <div style={{ color:'#64748b', marginBottom:8 }}>Show a warning when your balance is at or below this amount. Defaults to $0.50.</div>
            <div style={{ display:'flex', alignItems:'center', gap:12 }}>
              <input type="range" min={50} max={5000} step={50} value={activeSettings.lowBalanceCents}
                onChange={(e)=>{
                  const val = Number(e.target.value);
                  setActiveSettings({ lowBalanceCents: val });
                }} />
              <div style={{ minWidth: 70 }}><strong>{dollars(activeSettings.lowBalanceCents)}</strong></div>
              <button onClick={async()=>{
                try {
                  const uid = (user?.id || user?.userId || '').toString();
                  const key = uid ? `dashSettings:${uid}` : 'dashSettings';
                  localStorage.setItem(key, JSON.stringify({ lowBalanceCents: activeSettings.lowBalanceCents }));
                  // Persist to server
                  const t = authService.getToken && authService.getToken();
                  await fetch('/api/billing/low-balance-threshold', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json', ...(t ? { Authorization: `Bearer ${t}` } : {}) },
                    credentials: 'include',
                    body: JSON.stringify({ lowBalanceWarnCents: activeSettings.lowBalanceCents })
                  });
                  // re-check balance immediately after saving
                  const roles = (user?.roles || []).map(r => (r||'').toLowerCase());
                  const isPaid = roles.includes('paiduser') || roles.includes('admin');
                  sessionStorage.removeItem('lowBalanceDismissed');
                  if (isPaid && balance && typeof balance.balanceCents === 'number' && balance.balanceCents <= (activeSettings.lowBalanceCents||50)) {
                    setShowLowBalanceModal(true);
                  }
                } catch {}
              }}>Save</button>
            </div>
          </div>
          <div className="dashboard-card" style={{ marginTop: 12 }}>
            <h3 style={{ marginTop: 0 }}>Defaults</h3>
            <div style={{ display:'flex', gap:40, alignItems:'center', flexWrap:'wrap' }}>
              <label>Global Landing
                <select style={{ marginLeft:20 }} defaultValue={"/dashboard"} onChange={async(e)=>{
                  const val = e.target.value;
                  try {
                    const t = authService.getToken && authService.getToken();
                    await fetch('/api/userpreferences', { method:'POST', headers: { 'Content-Type':'application/json', ...(t?{ Authorization:`Bearer ${t}` }: {}) }, credentials:'include', body: JSON.stringify({ DefaultGlobalLanding: val, DefaultLandingTab: activeTab, DefaultDateRange: range }) });
                  } catch {}
                }}>
                  <option value="/dashboard">Dashboard</option>
                  <option value="/ai">AI</option>
                  <option value="/docs">Docs</option>
                </select>
              </label>
              <label>Dashboard Landing
                <select style={{ marginLeft:20 }} defaultValue={activeTab} onChange={async(e)=>{
                  const val = e.target.value;
                  try {
                    const t = authService.getToken && authService.getToken();
                    await fetch('/api/userpreferences', { method:'POST', headers: { 'Content-Type':'application/json', ...(t?{ Authorization:`Bearer ${t}` }: {}) }, credentials:'include', body: JSON.stringify({ DefaultLandingTab: val, DefaultDateRange: range }) });
                    setActiveTab(val);
                  } catch {}
                }}>
                  <option value="overview">Overview</option>
                  <option value="analytics">Analytics</option>
                  <option value="history">History</option>
                  <option value="insights">Insights</option>
                  <option value="settings">Settings</option>
                </select>
              </label>
              <label>Default Date Range
                <select style={{ marginLeft:20 }} defaultValue={range} onChange={async(e)=>{
                  const val = e.target.value;
                  try {
                    const t = authService.getToken && authService.getToken();
                    await fetch('/api/userpreferences', { method:'POST', headers: { 'Content-Type':'application/json', ...(t?{ Authorization:`Bearer ${t}` }: {}) }, credentials:'include', body: JSON.stringify({ DefaultLandingTab: activeTab, DefaultDateRange: val }) });
                    setRange(val);
                  } catch {}
                }}>
                  <option value="1d">1d</option>
                  <option value="7d">7d</option>
                  <option value="30d">30d</option>
                </select>
              </label>
              {/* Preferred Currency removed as per product decision */}
              <label>Default Export Format
                <select style={{ marginLeft:20 }} value={prefExportFormat} onChange={async(e)=>{
                  const v = e.target.value; setPrefExportFormat(v);
                  try {
                    const t = authService.getToken && authService.getToken();
                    await fetch('/api/userpreferences', { method:'POST', headers: { 'Content-Type':'application/json', ...(t?{ Authorization:`Bearer ${t}` }: {}) }, credentials:'include', body: JSON.stringify({ DefaultExportFormat: v }) });
                  } catch {}
                }}>
                  <option value="csv">CSV</option>
                  <option value="json">JSON</option>
                  <option value="txt">Text</option>
                  <option value="xlsx">Excel (.xlsx)</option>
                  <option value="docx">Word (.docx)</option>
                </select>
              </label>
              <label>Preferred Timezone
                <div style={{ display:'inline-flex', gap:8, marginLeft:20, alignItems:'center', flexWrap:'wrap' }}>
                  <input placeholder="Filter… (e.g., Bangkok, UTC+7)" value={tzFilter} onChange={(e)=>setTzFilter(e.target.value)} style={{ width:160 }} />
                  <select style={{ minWidth: 280 }} value={prefTimezone} onChange={async(e)=>{ 
                    const val = e.target.value; setPrefTimezone(val);
                    try {
                      const t = authService.getToken && authService.getToken();
                      await fetch('/api/userpreferences', { method:'POST', headers: { 'Content-Type':'application/json', ...(t?{ Authorization:`Bearer ${t}` }: {}) }, credentials:'include', body: JSON.stringify({ PreferredTimezone: val || null }) });
                    } catch {}
                  }}>
                    <option value="">System default</option>
                    {(timezoneOptions||[]).filter(o => {
                      const q = (tzFilter||'').toLowerCase();
                      if (!q) return true;
                      const offset = o.label.match(/UTC[+-]\d{1,2}(?::\d{2})?/);
                      return o.tz.toLowerCase().includes(q) || o.label.toLowerCase().includes(q) || (offset && offset[0].toLowerCase().includes(q));
                    }).map(o => (
                      <option key={o.tz} value={o.tz}>{o.label}</option>
                    ))}
                  </select>
                </div>
                <div style={{ marginLeft:20, marginTop:6, fontSize:12, color:'#64748b' }}>Current: {effectiveTimezoneLabel}</div>
              </label>
            </div>
          </div>
          <div className="dashboard-card" style={{ marginTop: 12 }}>
            <h3 style={{ marginTop: 0 }}>Spend Controls</h3>
            <div style={{ color:'#64748b', marginBottom:8 }}>Optional soft limits. Per-request cap blocks expensive requests during preflight; daily cap stops adding spend beyond the cap.</div>
            <div style={{ display:'flex', gap:40, alignItems:'center', flexWrap:'wrap' }}>
              <label>Per-Request Cap
                <select style={{ marginLeft:20 }} defaultValue={"0"} onChange={async(e)=>{
                  const val = Number(e.target.value);
                  try {
                    const t = authService.getToken && authService.getToken();
                    await fetch('/api/userpreferences', { method:'POST', headers: { 'Content-Type':'application/json', ...(t?{ Authorization:`Bearer ${t}` }: {}) }, credentials:'include', body: JSON.stringify({ PerRequestCapCents: val>0?val:null }) });
                  } catch {}
                }}>
                  <option value="0">Off</option>
                  <option value="25">$0.25</option>
                  <option value="50">$0.50</option>
                  <option value="100">$1.00</option>
                  <option value="200">$2.00</option>
                </select>
              </label>
              <label>Daily Spend Cap
                <select style={{ marginLeft:20 }} defaultValue={"0"} onChange={async(e)=>{
                  const val = Number(e.target.value);
                  try {
                    const t = authService.getToken && authService.getToken();
                    await fetch('/api/userpreferences', { method:'POST', headers: { 'Content-Type':'application/json', ...(t?{ Authorization:`Bearer ${t}` }: {}) }, credentials:'include', body: JSON.stringify({ DailySpendCapCents: val>0?val:null }) });
                  } catch {}
                }}>
                  <option value="0">Off</option>
                  <option value="200">$2</option>
                  <option value="500">$5</option>
                  <option value="1000">$10</option>
                  <option value="2000">$20</option>
                </select>
              </label>
            </div>
          </div>
          <div className="dashboard-card" style={{ marginTop: 12 }}>
            <h3 style={{ marginTop: 0 }}>Email Alerts</h3>
            <div style={{ color:'#64748b', marginBottom:8 }}>All optional emails are off by default. Legal notices (Terms, Policy, Disclaimer changes) are always sent.</div>
            <div style={{ display:'flex', gap:40, alignItems:'center', flexWrap:'wrap' }}>
              <label><input type="checkbox" checked={prefEmailLow} onChange={async(e)=>{
                const val = e.target.checked; setPrefEmailLow(val);
                try { const t = authService.getToken && authService.getToken(); await fetch('/api/userpreferences', { method:'POST', headers: { 'Content-Type':'application/json', ...(t?{ Authorization:`Bearer ${t}` }: {}) }, credentials:'include', body: JSON.stringify({ EmailLowBalance: val }) }); } catch {}
              }} /> <span style={{ marginLeft:8 }}>Low balance warning email</span></label>
              <label><input type="checkbox" checked={prefEmailWeekly} onChange={async(e)=>{
                const val = e.target.checked; setPrefEmailWeekly(val);
                try { const t = authService.getToken && authService.getToken(); await fetch('/api/userpreferences', { method:'POST', headers: { 'Content-Type':'application/json', ...(t?{ Authorization:`Bearer ${t}` }: {}) }, credentials:'include', body: JSON.stringify({ EmailWeeklySummary: val }) }); } catch {}
              }} /> <span style={{ marginLeft:8 }}>Weekly summary</span></label>
              <label><input type="checkbox" checked={prefEmailAnomaly} onChange={async(e)=>{
                const val = e.target.checked; setPrefEmailAnomaly(val);
                try { const t = authService.getToken && authService.getToken(); await fetch('/api/userpreferences', { method:'POST', headers: { 'Content-Type':'application/json', ...(t?{ Authorization:`Bearer ${t}` }: {}) }, credentials:'include', body: JSON.stringify({ EmailAnomalySpikes: val }) }); } catch {}
              }} /> <span style={{ marginLeft:8 }}>Anomaly spikes (sudden spend)</span></label>
              <label><input type="checkbox" checked={prefEmailReceipt} onChange={async(e)=>{
                const val = e.target.checked; setPrefEmailReceipt(val);
                try { const t = authService.getToken && authService.getToken(); await fetch('/api/userpreferences', { method:'POST', headers: { 'Content-Type':'application/json', ...(t?{ Authorization:`Bearer ${t}` }: {}) }, credentials:'include', body: JSON.stringify({ EmailReceiptOnPayment: val }) }); } catch {}
              }} /> <span style={{ marginLeft:8 }}>Email receipt for payments</span></label>
            </div>
          </div>
          <div className="dashboard-card" style={{ marginTop: 12 }}>
            <h3 style={{ marginTop: 0 }}>Password Policy</h3>
            <div style={{ color:'#64748b', marginBottom:8 }}>Optional rotation policy. None is recommended per modern guidance.</div>
            <div style={{ display:'flex', gap:40, alignItems:'center', flexWrap:'wrap' }}>
              <label>Force password change every
                <select style={{ marginLeft:20 }} value={prefPasswordRotation} onChange={async(e)=>{
                  const val = e.target.value; setPrefPasswordRotation(val);
                  try {
                    const t = authService.getToken && authService.getToken();
                    await fetch('/api/userpreferences', { method:'POST', headers: { 'Content-Type':'application/json', ...(t?{ Authorization:`Bearer ${t}` }: {}) }, credentials:'include', body: JSON.stringify({ PasswordRotationDays: (val==='none'? null : Number(val)) }) });
                  } catch {}
                }}>
                  <option value="none">None (recommended)</option>
                  <option value="90">90 days</option>
                  <option value="180">180 days</option>
                  <option value="365">365 days</option>
                </select>
              </label>
            </div>
          </div>
          <div className="dashboard-card" style={{ marginTop: 12 }}>
            <h3 style={{ marginTop: 0 }}>Model Preferences</h3>
            <div style={{ color:'#64748b', marginBottom:8 }}>Choose default provider/model and maximum output tokens. These are used on the AI page when you haven't chosen a model yet.</div>
            <div style={{ color:'#64748b', marginBottom:8 }}>For limits higher than 4096 tokens (~2800-3000 words), contact support@pocketpromptengineer.com</div>
            <div style={{ display:'flex', gap:40, alignItems:'center', flexWrap:'wrap' }}>
              <label>Default Provider
                <select style={{ marginLeft:20 }} value={prefProvider} onChange={async(e)=>{
                  const v = e.target.value;
                  const prov = (modelProviders||[]).find(p => p.key === v);
                  let nextModel = prefModel;
                  const allowed = prov ? (prov.allowed||[]) : [];
                  if (!allowed.some(m => String(m) === String(nextModel))) {
                    nextModel = prov ? (prov.defaultModel || (allowed[0] || '')) : '';
                  }
                  setPrefProvider(v);
                  setPrefModel(nextModel);
                  try {
                    const t = authService.getToken && authService.getToken();
                    await fetch('/api/userpreferences', { method:'POST', headers: { 'Content-Type':'application/json', ...(t?{ Authorization:`Bearer ${t}` }: {}) }, credentials:'include', body: JSON.stringify({ DefaultProvider: v, DefaultModel: nextModel || null }) });
                  } catch {}
                }}>
                  {(modelProviders||[]).map(p => (<option key={p.key} value={p.key}>{p.name}</option>))}
                </select>
              </label>
              <label>Default Model
                <select style={{ marginLeft:20 }} value={prefModel} onChange={async(e)=>{
                  const m = e.target.value;
                  setPrefModel(m);
                  try {
                    const t = authService.getToken && authService.getToken();
                    await fetch('/api/userpreferences', { method:'POST', headers: { 'Content-Type':'application/json', ...(t?{ Authorization:`Bearer ${t}` }: {}) }, credentials:'include', body: JSON.stringify({ DefaultProvider: prefProvider || null, DefaultModel: m }) });
                  } catch {}
                }}>
                  {(() => {
                    const prov = (modelProviders||[]).find(p => p.key === prefProvider);
                    const allowed = prov ? (prov.allowed||[]) : [];
                    return allowed.map(m => (<option key={m} value={m}>{m}</option>));
                  })()}
                </select>
              </label>
              <label>Max Output Tokens
                <input style={{ marginLeft:20, width: 120 }} type="number" min={64} max={4096} step={64} placeholder="e.g., 512" value={prefMaxTokens} onChange={(e)=>setPrefMaxTokens(e.target.value)} onBlur={async(e)=>{
                  const v = parseInt(e.target.value||'0', 10) || null;
                  try { const t = authService.getToken && authService.getToken(); await fetch('/api/userpreferences', { method:'POST', headers: { 'Content-Type':'application/json', ...(t?{ Authorization:`Bearer ${t}` }: {}) }, credentials:'include', body: JSON.stringify({ DefaultMaxOutputTokens: v }) }); } catch {}
                }} />
              </label>
            </div>
          </div>
          <div className="dashboard-card" style={{ marginTop: 12 }}>
            <h3 style={{ marginTop: 0 }}>Saved Prompts Default Visibility</h3>
            <div style={{ color:'#64748b', marginBottom:8 }}>Choose the default privacy for new saved prompts.</div>
            <div style={{ display:'flex', gap:40, alignItems:'center', flexWrap:'wrap' }}>
              <label>Default Visibility
                <select style={{ marginLeft:20 }} value={prefDefaultPromptVisibility===null? 'unset' : (prefDefaultPromptVisibility ? 'public' : 'private')} onChange={async(e)=>{
                  const v = e.target.value;
                  const choice = v==='unset' ? null : (v==='public');
                  setPrefDefaultPromptVisibility(choice);
                  try {
                    const t = authService.getToken && authService.getToken();
                    await fetch('/api/userpreferences', { method:'POST', headers: { 'Content-Type':'application/json', ...(t?{ Authorization:`Bearer ${t}` }: {}) }, credentials:'include', body: JSON.stringify({ DefaultSavedPromptIsPublic: choice }) });
                  } catch {}
                }}>
                  <option value="unset">No default (ask/decide per save)</option>
                  <option value="private">Private</option>
                  <option value="public">Public</option>
                </select>
              </label>
            </div>
          </div>

          <div className="dashboard-card" style={{ marginTop: 12 }}>
            <h3 style={{ marginTop: 0 }}>Account</h3>
            {(() => {
              const isTeamOrEnterprise = user && (isTeamUser(user) || isEnterpriseUser(user));
              
              if (isTeamOrEnterprise) {
                // Team/Enterprise Users: Show Deactivate Account
                return (
                  <>
                    <div style={{ color:'#64748b', marginBottom:8 }}>
                      <strong>Deactivate your account</strong>
                    </div>
                    <div style={{ color:'#92400e', background:'#fffbeb', border:'1px solid #fde68a', padding:'10px 12px', borderRadius:8, marginBottom:10 }}>
                      This will remove your access to the application but will not delete any of your work or data. Your account can be reactivated by your team or organization administrator if needed.
                    </div>
                    <div>
                      <button
                        className="action-button"
                        style={{ background:'#f59e0b', color:'#fff', border:'1px solid #f59e0b' }}
                        onClick={()=>setShowDeleteModal(true)}
                      >Deactivate Account</button>
                    </div>
                  </>
                );
              } else {
                // Free/Pro Users: Show Delete Account
                return (
                  <>
                    <div style={{ color:'#64748b', marginBottom:8 }}>
                      <strong>Remove your account permanently</strong>
                    </div>
                    <div style={{ color:'#991b1b', background:'#fef2f2', border:'1px solid #fecaca', padding:'10px 12px', borderRadius:8, marginBottom:10 }}>
                      You are permanently deleting your account and all of its associated data. We will not be able to assist you in any account matters including billing, prompts or saved output. Any balance of money you have cannot be refunded.
                    </div>
                    <div>
                      <button
                        className="action-button danger"
                        disabled={(() => { try { const em=(user?.email||''); return em.startsWith('TestDrive') && em.endsWith('@pocketpromptengineer.com'); } catch { return false; } })()}
                        onClick={()=>setShowDeleteModal(true)}
                      >Delete Account</button>
                    </div>
                  </>
                );
              }
            })()}
          </div>
        </div>
      )}

      {showContact && (
        <div className="modal-backdrop" onClick={()=>setShowContact(false)}>
          <div className="app-modal" onClick={e=>e.stopPropagation()}>
            <div className="app-modal-header">
              <strong>Contact</strong>
              <button className="close" onClick={()=>setShowContact(false)}>×</button>
            </div>
            <div className="app-modal-body">
              <p>For all billing and usage issues, technical problems, comments and suggestions, contact us via email at <a href="mailto:support@pocketpromptengineer.com">support@pocketpromptengineer.com</a>.</p>
            </div>
          </div>
        </div>
      )}

      {showDeleteModal && (() => {
        const isTeamOrEnterprise = user && (isTeamUser(user) || isEnterpriseUser(user));
        
        if (isTeamOrEnterprise) {
          // Team/Enterprise Users: Deactivate Account Modal
          return (
            <div className="modal-backdrop" onClick={(e)=>e.stopPropagation()}>
              <div className="app-modal" onClick={(e)=>e.stopPropagation()}>
                <div className="app-modal-header">
                  <strong>Confirm Account Deactivation</strong>
                </div>
                <div className="app-modal-body">
                  <p>This will remove your access to the application but will not delete any of your work or data. Your account can be reactivated by your team or organization administrator if needed. Your work, prompts, and saved output will remain intact.</p>
                </div>
                <div className="app-modal-footer" style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
                  <button onClick={()=>setShowDeleteModal(false)}>Cancel</button>
                  <button 
                    style={{ background:'#f59e0b', color:'#fff', border:'1px solid #f59e0b' }}
                    onClick={async()=>{
                      setShowDeleteModal(false);
                      setShowDeleting(true);
                      let ok=false; let message='';
                      try{
                        const t = authService.getToken && authService.getToken();
                        const res = await fetch('/api/auth/deactivate-account', { method:'POST', headers: { ...(t?{ Authorization:`Bearer ${t}` }: {}) }, credentials: 'include' });
                        if(res.ok){ 
                          const j=await res.json(); 
                          ok=true; 
                          message='Your account has been deactivated. You will be logged out shortly.';
                        } else {
                          const j = await res.json().catch(() => ({}));
                          message = j.message || 'Failed to deactivate account. Please try again.';
                        }
                      }catch(err){
                        message = 'An error occurred while deactivating your account. Please try again.';
                      }
                      setShowDeleting(false);
                      setDeleteResult({ ok, deletionId: '', message });
                      setShowDeletedModal(true);
                    }}
                  >Yes, deactivate my account</button>
                </div>
              </div>
            </div>
          );
        } else {
          // Free/Pro Users: Delete Account Modal
          return (
            <div className="modal-backdrop" onClick={(e)=>e.stopPropagation()}>
              <div className="app-modal" onClick={(e)=>e.stopPropagation()}>
                <div className="app-modal-header">
                  <strong>Confirm Permanent Deletion</strong>
                </div>
                <div className="app-modal-body">
                  <p>You are permanently deleting your account and all of its associated data. We will not be able to assist you in any account matters including billing, prompts or saved output. Any balance of money you have cannot be refunded. Confirm this action.</p>
                </div>
                <div className="app-modal-footer" style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
                  <button onClick={()=>setShowDeleteModal(false)}>Cancel</button>
                  <button className="danger" onClick={async()=>{
                    setShowDeleteModal(false);
                    setShowDeleting(true);
                    let ok=false; let deletionId='';
                    try{
                      const t = authService.getToken && authService.getToken();
                      const res = await fetch('/api/auth/delete-account', { method:'POST', headers: { ...(t?{ Authorization:`Bearer ${t}` }: {}) } });
                      if(res.ok){ const j=await res.json(); ok=true; deletionId=j.deletionId||''; }
                    }catch{}
                    setShowDeleting(false);
                    setDeleteResult({ ok, deletionId });
                    setShowDeletedModal(true);
                  }}>Yes, permanently delete my account</button>
                </div>
              </div>
            </div>
          );
        }
      })()}

      {showDeleting && (
        <div className="modal-backdrop">
          <div className="app-modal">
            <div className="app-modal-header"><strong>{(() => {
              const isTeamOrEnterprise = user && (isTeamUser(user) || isEnterpriseUser(user));
              return isTeamOrEnterprise ? 'Deactivating Account…' : 'Deleting Account…';
            })()}</strong></div>
            <div className="app-modal-body"><p>Please wait…</p></div>
          </div>
        </div>
      )}

      {showDeletedModal && (() => {
        const isTeamOrEnterprise = user && (isTeamUser(user) || isEnterpriseUser(user));
        
        if (isTeamOrEnterprise) {
          // Team/Enterprise Users: Deactivation Success Modal
          return (
            <div className="modal-backdrop">
              <div className="app-modal">
                <div className="app-modal-header"><strong>Account Deactivated</strong></div>
                <div className="app-modal-body">
                  <p>{_deleteResult.message || 'Your account has been successfully deactivated. Your work and data remain intact and can be accessed again if your account is reactivated by your administrator.'}</p>
                </div>
                <div className="app-modal-footer" style={{ display:'flex', justifyContent:'flex-end' }}>
                  <button className="primary" onClick={async()=>{
                    setShowDeletedModal(false);
                    const timer = setTimeout(async()=>{ try{ await authService.logout(); window.location.href='/login'; } catch{} }, 0);
                    setTimeout(async()=>{ try{ clearTimeout(timer); await authService.logout(); window.location.href='/login'; } catch{} }, 10000);
                  }}>OK</button>
                </div>
              </div>
            </div>
          );
        } else {
          // Free/Pro Users: Deletion Success Modal
          return (
            <div className="modal-backdrop">
              <div className="app-modal">
                <div className="app-modal-header"><strong>Account Deleted</strong></div>
                <div className="app-modal-body"><p>Your account has been successfully deleted.</p></div>
                <div className="app-modal-footer" style={{ display:'flex', justifyContent:'flex-end' }}>
                  <button className="primary" onClick={async()=>{
                    setShowDeletedModal(false);
                    const timer = setTimeout(async()=>{ try{ await authService.logout(); window.location.href='/login'; } catch{} }, 0);
                    setTimeout(async()=>{ try{ clearTimeout(timer); await authService.logout(); window.location.href='/login'; } catch{} }, 10000);
                  }}>OK</button>
                </div>
              </div>
            </div>
          );
        }
      })()}

      {showLowBalanceModal && (
        <div className="modal-backdrop" onClick={()=>{ 
          setShowLowBalanceModal(false); 
          sessionStorage.setItem('lowBalanceDismissed','true'); 
          window.dispatchEvent(new CustomEvent('lowBalanceDismissed'));
        }}>
          <div className="app-modal" onClick={e=>e.stopPropagation()}>
            <div className="app-modal-header">
              <strong>Low Balance Warning</strong>
              <button className="close" onClick={()=>{ 
                setShowLowBalanceModal(false); 
                sessionStorage.setItem('lowBalanceDismissed','true'); 
                window.dispatchEvent(new CustomEvent('lowBalanceDismissed'));
              }}>×</button>
            </div>
            <div className="app-modal-body">
              <p>Your balance is below your warning level of <strong>{dollars(activeSettings.lowBalanceCents)}</strong>. Consider adding funds.</p>
              <div style={{ textAlign:'right', marginTop:12 }}>
                <button className="primary" onClick={()=>{ 
                  setShowLowBalanceModal(false); 
                  sessionStorage.setItem('lowBalanceDismissed','true'); 
                  window.dispatchEvent(new CustomEvent('lowBalanceDismissed'));
                }}>OK</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Toast Components */}
      <Toast 
        message={toast.message} 
        type={toast.type} 
        show={toast.show} 
        onClose={() => setToast({ ...toast, show: false })} 
      />
      <Toast 
        message={confirmToast.message} 
        type="warning" 
        show={confirmToast.show} 
        persist={true}
        actionLabel="Confirm"
        onAction={confirmToast.onConfirm}
        secondaryActionLabel="Cancel"
        onSecondaryAction={confirmToast.onCancel}
        onClose={() => setConfirmToast({ ...confirmToast, show: false })} 
      />
    </div>
  );
};

export default NewDashboard;


